import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shovel, Trees, CheckCircle, Phone } from 'lucide-react';
import './Services.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Default images
const DEFAULT_ERDARBEITEN_IMAGE = 'https://customer-assets.emergentagent.com/job_2fdbb258-2fe3-4bcb-af8e-a32c1bf06202/artifacts/xuzlwsfh_WhatsApp%20Image%202026-03-21%20at%2015.32.59.jpeg';
const DEFAULT_GARTENPFLEGE_IMAGE = 'https://customer-assets.emergentagent.com/job_2fdbb258-2fe3-4bcb-af8e-a32c1bf06202/artifacts/mg3au26l_WhatsApp%20Image%202026-03-21%20at%2015.53.49.jpeg';

// Convert position to CSS object-position
const getPositionStyle = (position) => {
  switch (position) {
    case 'top': return 'center 30%';
    case 'bottom': return 'center 70%';
    default: return 'center 50%';
  }
};

const Services = () => {
  const [homepageImages, setHomepageImages] = useState({
    erdarbeiten: { image_url: DEFAULT_ERDARBEITEN_IMAGE, position: 'center' },
    gartenpflege: { image_url: DEFAULT_GARTENPFLEGE_IMAGE, position: 'bottom' }
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Leistungen | Nessenius Garten & Landschaftsbau';
    
    // Fetch homepage images for consistent look
    const fetchImages = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/homepage-images`);
        if (response.ok) {
          const data = await response.json();
          setHomepageImages(data);
        }
      } catch (error) {
        console.error('Error fetching images:', error);
      }
    };
    fetchImages();
  }, []);

  return (
    <div className="services-page-new">
      {/* Hero Section */}
      <section className="services-hero">
        <div className="container">
          <h1>Unsere Leistungen</h1>
          <p>Zwei Bereiche – ein Ziel: Ihr Grundstück in besten Händen</p>
        </div>
      </section>

      {/* Two Main Services */}
      <section className="services-main">
        <div className="container">
          <div className="services-two-columns">
            
            {/* ERDARBEITEN */}
            <div className="service-column erdarbeiten">
              <div className="service-column-image">
                <img 
                  src={homepageImages.erdarbeiten.image_url} 
                  alt="Erdarbeiten & Gartenbau"
                  style={{ objectPosition: getPositionStyle(homepageImages.erdarbeiten.position) }}
                />
                <div className="service-column-icon">
                  <Shovel size={32} />
                </div>
              </div>
              <div className="service-column-content">
                <h2>Erdarbeiten & Gartenbau</h2>
                <p className="service-column-intro">
                  Mit Minibagger und Kipper führen wir Aushub, Geländevorbereitung und Bauarbeiten zuverlässig aus.
                </p>
                
                <div className="service-column-features">
                  <div className="feature-group">
                    <h4>Bagger- & Erdarbeiten</h4>
                    <ul>
                      <li><CheckCircle size={16} /> Baggerarbeiten mit Minibagger</li>
                      <li><CheckCircle size={16} /> Aushub / Auskoffern</li>
                      <li><CheckCircle size={16} /> Erdtransport mit Kipper</li>
                    </ul>
                  </div>
                  <div className="feature-group">
                    <h4>Baugruben & Geländearbeiten</h4>
                    <ul>
                      <li><CheckCircle size={16} /> Baugruben (Gartenhaus, Pool)</li>
                      <li><CheckCircle size={16} /> Planieren & Einebnen</li>
                      <li><CheckCircle size={16} /> Hangabfangungen</li>
                    </ul>
                  </div>
                  <div className="feature-group">
                    <h4>Mauern & Entwässerung</h4>
                    <ul>
                      <li><CheckCircle size={16} /> Winkelstützen & Mauern</li>
                      <li><CheckCircle size={16} /> Drainage verlegen</li>
                      <li><CheckCircle size={16} /> Pflastervorbereitung</li>
                    </ul>
                  </div>
                </div>

                <Link to="/leistungen/erdarbeiten" className="service-column-btn">
                  Alle Erdarbeiten ansehen
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            {/* GARTENPFLEGE */}
            <div className="service-column gartenpflege">
              <div className="service-column-image">
                <img 
                  src={homepageImages.gartenpflege.image_url} 
                  alt="Gartenpflege"
                  style={{ objectPosition: getPositionStyle(homepageImages.gartenpflege.position) }}
                />
                <div className="service-column-icon">
                  <Trees size={32} />
                </div>
              </div>
              <div className="service-column-content">
                <h2>Gartenpflege</h2>
                <p className="service-column-intro">
                  Von regelmäßigem Rasenmähen bis zum Heckenschnitt – wir sorgen für ein gepflegtes Gesamtbild.
                </p>
                
                <div className="service-column-features">
                  <div className="feature-group">
                    <h4>Rasenpflege</h4>
                    <ul>
                      <li><CheckCircle size={16} /> Rasen mähen (einmalig/regelmäßig)</li>
                      <li><CheckCircle size={16} /> Rasenneuanlage</li>
                      <li><CheckCircle size={16} /> Rasenkanten schneiden</li>
                    </ul>
                  </div>
                  <div className="feature-group">
                    <h4>Hecken & Bäume</h4>
                    <ul>
                      <li><CheckCircle size={16} /> Hecken schneiden</li>
                      <li><CheckCircle size={16} /> Baumschnitt (kleinere Bäume)</li>
                      <li><CheckCircle size={16} /> Obstbaumschnitt</li>
                    </ul>
                  </div>
                  <div className="feature-group">
                    <h4>Allgemeine Pflege</h4>
                    <ul>
                      <li><CheckCircle size={16} /> Beetpflege & Unkraut</li>
                      <li><CheckCircle size={16} /> Laubarbeiten im Herbst</li>
                      <li><CheckCircle size={16} /> Grünschnittentsorgung</li>
                    </ul>
                  </div>
                </div>

                <Link to="/leistungen/gartenpflege" className="service-column-btn">
                  Alle Gartenpflege-Leistungen
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="services-additional">
        <div className="container">
          <h2>Zusätzliche Leistungen</h2>
          <p className="services-additional-intro">
            Neben unseren zwei Hauptbereichen bieten wir weitere Dienstleistungen an:
          </p>
          <div className="additional-services-grid">
            <div className="additional-service-card">
              <CheckCircle size={24} />
              <h3>Baumstumpf fräsen</h3>
              <p>Entfernung von Wurzelstöcken nach Baumfällung</p>
            </div>
            <div className="additional-service-card">
              <CheckCircle size={24} />
              <h3>Rückbau / Abbruch</h3>
              <p>Kleinere Abbrucharbeiten im Gartenbereich</p>
            </div>
            <div className="additional-service-card">
              <CheckCircle size={24} />
              <h3>Entsorgung</h3>
              <p>Grünschnitt, Erdaushub, Bauschutt entsorgen</p>
            </div>
            <div className="additional-service-card">
              <CheckCircle size={24} />
              <h3>Hochdruckreinigung</h3>
              <p>Terrassen, Einfahrten und Pflasterflächen</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta">
        <div className="container">
          <h2>Welches Projekt haben Sie?</h2>
          <p>Erzählen Sie uns von Ihrem Vorhaben – wir beraten Sie gerne und machen Ihnen ein faires Angebot.</p>
          <div className="services-cta-buttons">
            <a href="tel:+4917655669090" className="btn-cta">
              <Phone size={18} />
              0176 - 556 690 90
            </a>
            <Link to="/kontakt" className="btn-secondary">
              Kontaktformular
            </Link>
          </div>
        </div>
      </section>

      {/* Local Service Links */}
      <section className="services-local-section">
        <div className="container">
          <h2>Unsere Leistungen in Ihrer Nähe</h2>
          <div className="services-local-grid">
            {[
              ['/gartenpflege/goettingen', 'Gartenpflege Göttingen'],
              ['/erdarbeiten/goettingen', 'Erdarbeiten Göttingen'],
              ['/baumfaellung/goettingen', 'Baumfällung Göttingen'],
              ['/gartenpflege/northeim', 'Gartenpflege Northeim'],
              ['/erdarbeiten/northeim', 'Erdarbeiten Northeim'],
              ['/gartenpflege/duderstadt', 'Gartenpflege Duderstadt'],
              ['/erdarbeiten/duderstadt', 'Erdarbeiten Duderstadt'],
              ['/baumfaellung/duderstadt', 'Baumfällung Duderstadt'],
              ['/gartenpflege/einbeck', 'Gartenpflege Einbeck'],
              ['/erdarbeiten/einbeck', 'Erdarbeiten Einbeck'],
              ['/gartenpflege/bovenden', 'Gartenpflege Bovenden'],
              ['/erdarbeiten/bovenden', 'Erdarbeiten Bovenden'],
              ['/hochdruckreinigung/goettingen', 'Hochdruckreinigung Göttingen'],
              ['/gartenpflege/osterode', 'Gartenpflege Osterode'],
              ['/erdarbeiten/herzberg', 'Erdarbeiten Herzberg'],
              ['/gartenpflege/hardegsen', 'Gartenpflege Hardegsen'],
            ].map(([path, label]) => (
              <Link key={path} to={path} className="services-local-link">{label}</Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
